import type { ProjectListItem } from '@fos/shared/contracts/projects';
import { useMemo } from 'react';
import { trpc } from '../../../lib/trpc';
import { Combobox, type ComboboxOption } from '../../../ui/Combobox';
import { ClientDot } from './ProjectBits';

const NONE = '__none__';

export type ProjectOption = ProjectListItem;

/**
 * Wybór projektu (formularze zadań, czasu, zasobów). Lista `projects.options`: otwarte projekty z zakresu osoby,
 * dostępna przy podglądzie Projektów, Zadań albo Czasu. `clientId` zawęża do projektów klienta
 * (`undefined` = wszystkie, wtedy opis pokazuje klienta). `onValueChange(id, project)` zwraca też projekt,
 * żeby formularz mógł uzupełnić klienta (zadanie nie wskaże projektu innego klienta).
 */
export function ProjectPicker({
  value,
  onValueChange,
  clientId,
  includeClosed = false,
  allowEmpty = true,
  emptyLabel = 'Bez projektu',
  placeholder = 'Wybierz projekt',
  disabled,
  size,
  id,
  className,
  'aria-label': ariaLabel,
}: {
  value: string | null;
  onValueChange: (projectId: string | null, project: ProjectOption | null) => void;
  /** projekty jednego klienta; `undefined` = wszystkie widoczne */
  clientId?: string;
  includeClosed?: boolean;
  /** pozycja „Bez projektu” na początku listy */
  allowEmpty?: boolean;
  emptyLabel?: string;
  placeholder?: string;
  disabled?: boolean;
  size?: 's' | 'm';
  id?: string;
  className?: string;
  'aria-label'?: string;
}) {
  const query = trpc.projects.options.useQuery({ clientId, includeClosed });
  const projects = query.data ?? [];

  const options = useMemo<ComboboxOption[]>(() => {
    const list: ComboboxOption[] = (query.data ?? []).map((p) => ({
      value: p.id,
      label: p.name,
      description: clientId ? undefined : (p.client?.name ?? (p.isInternal ? 'wewnętrzny' : undefined)),
      leading: <ClientDot client={p.client} internal={p.isInternal} />,
      keywords: p.client ? [p.client.name] : [],
    }));
    return allowEmpty ? [{ value: NONE, label: emptyLabel }, ...list] : list;
  }, [query.data, clientId, allowEmpty, emptyLabel]);

  let emptyText = 'Brak projektów';
  if (query.isPending) emptyText = 'Wczytywanie…';
  else if (query.isError) emptyText = 'Nie udało się wczytać projektów';
  else if (clientId) emptyText = 'Ten klient nie ma otwartych projektów';

  return (
    <Combobox
      id={id}
      aria-label={ariaLabel}
      className={className}
      size={size}
      disabled={disabled}
      value={value ?? (allowEmpty ? NONE : null)}
      onValueChange={(v) => {
        if (v === NONE) onValueChange(null, null);
        else onValueChange(v, projects.find((p) => p.id === v) ?? null);
      }}
      options={options}
      placeholder={query.isPending ? 'Wczytywanie…' : placeholder}
      searchPlaceholder="Szukaj projektu…"
      emptyText={emptyText}
    />
  );
}
